/**
 * AdvancesV2 — Vue mensuelle des acomptes récurrents intelligents.
 * KPI + Table records du mois avec actions Payer / Skip / Reset / Envoyer rappel.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, CircleDollarSign, RotateCw, SkipForward, Bell, Settings, Users2, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending:  { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "À payer" },
  paid:     { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Payé" },
  skipped:  { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Passé" },
};
const MONTHS = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const fmtEUR = (v) => v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function AdvancesV2() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [status, setStatus] = useState("all");
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({ pending: 0, paid: 0, skipped: 0, total: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (status !== "all") params.status = status;
      const [r, s] = await Promise.all([
        api.get("/hr/advance-records", { params }),
        api.get("/hr/advance-records/monthly-status", { params: { year, month } }),
      ]);
      setRecords(r.data.records || []);
      setStats(s.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, status]);

  const act = async (fn) => { try { await fn(); load(); } catch (e) { toast.error(apiError(e)); } };
  const markPaid = (id) => act(async () => { await api.post(`/hr/advance-records/${id}/mark-paid`); toast.success("Marqué payé"); });
  const skip = (id) => act(async () => { await api.post(`/hr/advance-records/${id}/skip`); toast.success("Passé"); });
  const reset = (id) => act(async () => { await api.post(`/hr/advance-records/${id}/reset`); toast.success("Réinitialisé"); });

  const generateMonthly = async () => {
    setTriggering(true);
    try { const r = await api.post("/hr/advance-records/generate-monthly", null, { params: { year, month } });
      toast.success(`${r.data.created} acompte(s) généré(s)`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setTriggering(false); }
  };
  const sendReminders = async () => {
    setTriggering(true);
    try { const r = await api.post("/hr/advance-records/send-reminders");
      toast.success(`${r.data.sent} rappel(s) envoyé(s) · ${r.data.skipped} sautés`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setTriggering(false); }
  };

  return (
    <div className="space-y-6" data-testid="advances-v2-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Acomptes récurrents
          </h1>
          <p className="text-sm text-slate-500">
            {MONTHS[month - 1]} {year} · {stats.count} acompte{stats.count > 1 ? "s" : ""} · {fmtEUR(stats.total)} au total
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate("/app/hr/advances-configs")}
            data-testid="btn-configs">
            <Users2 className="w-4 h-4 mr-1.5" /> Configs récurrentes
          </Button>
          <Button variant="outline" onClick={() => navigate("/app/hr/settings")}
            data-testid="btn-hr-settings">
            <Settings className="w-4 h-4 mr-1.5" /> Paramètres
          </Button>
          {canWrite && (
            <>
              <Button variant="outline" onClick={generateMonthly} disabled={triggering}
                data-testid="btn-generate-monthly">
                <RotateCw className="w-4 h-4 mr-1.5" /> Générer le mois
              </Button>
              <Button onClick={sendReminders} disabled={triggering}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-send-reminders">
                <Send className="w-4 h-4 mr-1.5" /> Envoyer rappels
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-slate-500 uppercase">À payer</p>
          <p className="text-2xl font-bold text-amber-600 font-mono">{stats.pending}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-slate-500 uppercase">Payés</p>
          <p className="text-2xl font-bold text-emerald-600 font-mono">{stats.paid}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-slate-500 uppercase">Passés</p>
          <p className="text-2xl font-bold text-slate-500 font-mono">{stats.skipped}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-slate-500 uppercase">Rappels envoyés</p>
          <p className="text-sm font-mono">J-2: <b>{stats.reminders_sent?.j2 || 0}</b> · J-1: <b>{stats.reminders_sent?.j1 || 0}</b> · J: <b>{stats.reminders_sent?.j0 || 0}</b></p>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-3 gap-2">
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger data-testid="filter-year"><SelectValue /></SelectTrigger>
              <SelectContent>{[year - 1, year, year + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger data-testid="filter-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="pending">À payer</SelectItem>
                <SelectItem value="paid">Payés</SelectItem>
                <SelectItem value="skipped">Passés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />)}</div>
          ) : records.length === 0 ? (
            <div className="p-12 text-center" data-testid="advances-v2-empty">
              <Wallet className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucun acompte ce mois-ci.</p>
              {canWrite && (
                <Button variant="link" onClick={generateMonthly}>Générer les acomptes du mois</Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">Salarié</TableHead>
                <TableHead className="text-xs uppercase text-right">Montant</TableHead>
                <TableHead className="text-xs uppercase">Échéance</TableHead>
                <TableHead className="text-xs uppercase">Rappels</TableHead>
                <TableHead className="text-xs uppercase">Statut</TableHead>
                <TableHead className="text-xs uppercase text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {records.map((r, i) => {
                  const st = STATUS[r.status] || STATUS.pending;
                  return (
                    <motion.tr key={r.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i, 10) * 0.02, duration: 0.2 }}
                      className="border-b hover:bg-slate-50" data-testid={`record-row-${i}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900 text-sm">{r.employee_name}</div>
                        <div className="text-xs text-slate-500">{r.job_title || "—"}</div>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">{fmtEUR(r.amount)}</TableCell>
                      <TableCell className="text-sm font-mono">{r.due_date && new Date(r.due_date).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {r.reminder_j2_sent && <Badge variant="outline" className="text-[10px] bg-blue-50">J-2 ✓</Badge>}
                          {r.reminder_j1_sent && <Badge variant="outline" className="text-[10px] bg-amber-50">J-1 ✓</Badge>}
                          {r.reminder_j0_sent && <Badge variant="outline" className="text-[10px] bg-red-50">J ✓</Badge>}
                          {!r.reminder_j2_sent && !r.reminder_j1_sent && !r.reminder_j0_sent && <span className="text-xs text-slate-400">—</span>}
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className={st.cls}>{st.label}</Badge>
                        {r.paid_via_magic_link && <Badge variant="outline" className="ml-1 text-[9px] bg-purple-50 text-purple-700 border-purple-200">Magic-link</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          {canWrite && r.status === "pending" && (
                            <>
                              <Button size="sm" variant="outline"
                                className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700"
                                onClick={() => markPaid(r.id)} data-testid={`pay-${r.id}`}>
                                <CircleDollarSign className="w-3.5 h-3.5 mr-1" />Payer
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-slate-500"
                                onClick={() => skip(r.id)}><SkipForward className="w-3.5 h-3.5" /></Button>
                            </>
                          )}
                          {canWrite && r.status !== "pending" && (
                            <Button size="sm" variant="ghost" className="h-7 text-slate-500"
                              onClick={() => reset(r.id)}><RotateCw className="w-3.5 h-3.5" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </motion.tr>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
